import React from 'react';
import styled, { css } from 'styled-components';

import { parseResourceCtaAction, type ResourceCtaActionHandlers } from 'app/features/resources/cta/actions';
import Button from 'app/ui/components/atoms/button';
import Typography from 'app/ui/components/atoms/typography';

type ResourceCtaProps = {
  label?: string | null;
  link?: string | null;
  appendPortalGymCode?: boolean;
  portalGymCode?: string | null;
  portalUserCode?: string | null;
  actionHandlers?: ResourceCtaActionHandlers;
};

const ctaSpacing = css`
  ${({ theme: { spacing, media } }) => `
   width: 100%;
   margin: ${spacing(7)} 0 ${spacing(3)} 0;

  ${media.medium(
    () => `
  width: auto;
  margin: ${spacing(14)} 0 ${spacing(6)} 0;
  `,
  )}
  `}
`;

const CTALink = styled.a`
  ${ctaSpacing}
`;

const CTAButtonWrapper = styled.div`
  ${ctaSpacing}
`;

const CTAButton = styled(Button)`
  width: 100%;
`;

const ErrorMessage = styled.div`
  ${({ theme: { spacing } }) => `
    margin-top: ${spacing(2)};
  `}
`;

const hasInvalidWhitespace = (link: string) => /\s/.test(link);
const EMPTY_ACTION_HANDLERS: ResourceCtaActionHandlers = {};

const formatHref = (url: URL, originalLink: string): string => {
  if (url.origin === window.location.origin && originalLink.startsWith('/')) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  return url.toString();
};

const resolveHref = ({
  link,
  appendPortalGymCode,
  portalGymCode,
  portalUserCode,
}: Pick<ResourceCtaProps, 'link' | 'appendPortalGymCode' | 'portalGymCode' | 'portalUserCode'>): string | null => {
  if (!link || hasInvalidWhitespace(link)) {
    return null;
  }

  try {
    const url = new URL(link, window.location.origin);
    const isHttpUrl = ['http:', 'https:'].includes(url.protocol);
    const isRelativeUrl = url.origin === window.location.origin && link.startsWith('/');

    if (!isHttpUrl && !isRelativeUrl) {
      return null;
    }

    if (appendPortalGymCode && (portalGymCode || portalUserCode)) {
      if (portalGymCode) {
        url.searchParams.set('code', portalGymCode);
      } else if (portalUserCode) {
        url.searchParams.set('userCode', portalUserCode);
      }
    }

    return formatHref(url, link);
  } catch {
    return null;
  }
};

const ResourceCta: React.FC<ResourceCtaProps> = ({
  label,
  link,
  appendPortalGymCode = false,
  portalGymCode,
  portalUserCode,
  actionHandlers = EMPTY_ACTION_HANDLERS,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!label || !link) {
    return null;
  }

  const actionName = parseResourceCtaAction(link);
  const actionHandler = actionName ? actionHandlers[actionName] : null;

  if (actionName) {
    if (!actionHandler) {
      return null;
    }

    return (
      <CTAButtonWrapper>
        <CTAButton
          label={label}
          state={isSubmitting ? 'disabled' : 'default'}
          isLoading={isSubmitting}
          onClick={async () => {
            const targetWindow = window.open('', '_blank', 'noopener,noreferrer');

            setIsSubmitting(true);
            setError(null);

            try {
              await actionHandler({ targetWindow });
            } catch {
              targetWindow?.close();
              setError('Something went wrong. Please try again.');
            } finally {
              setIsSubmitting(false);
            }
          }}
        />
        {error && (
          <ErrorMessage role="alert">
            <Typography variant="body3" color="#F03E3E">
              {error}
            </Typography>
          </ErrorMessage>
        )}
      </CTAButtonWrapper>
    );
  }

  const href = resolveHref({
    link,
    appendPortalGymCode,
    portalGymCode,
    portalUserCode,
  });

  if (!href) {
    return null;
  }

  return (
    <CTALink href={href} target="_blank" rel="noopener noreferrer">
      <CTAButton label={label} />
    </CTALink>
  );
};

export default ResourceCta;
